import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Box, Card, Typography, Chip, Stack, Button, Autocomplete, TextField
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import Spinner from '../Spinner';
import { FunctionalityDTO, ScopeDTO, ScopeFunctionalityDTO } from 'axiosApi/models';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { ENTITY_TYPE } from 'common/constants';
import { useDialog } from 'common/DialogProvider';
import intl from 'components/i18n/ReactIntlWrapper';

const TYPE_LABELS: Record<number, string> = { 1: 'API Endpoint', 2: 'UI Component', 3: 'Business Rule' };

const FunctionalityDetail = () => {
  const api: Api = useApi();
  const navigate = useNavigate();
  const toast = useToastMessageQueue();
  const { openDialog, closeDialog } = useDialog();
  const { id } = useParams();
  const idNumber = parseInt(id || '0');

  const [functionality, setFunctionality] = useState<FunctionalityDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState<ScopeFunctionalityDTO[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);

  const [scopeOptions, setScopeOptions] = useState<ScopeDTO[]>([]);
  const [selectedScope, setSelectedScope] = useState<ScopeDTO | null>(null);
  const [scopeSearchTerm, setScopeSearchTerm] = useState('');

  const fetchFunctionality = async () => {
    setLoading(true);
    try {
      const res = await api.employeeApi.apiVversionFunctionalityIdGet(idNumber, '1', {});
      setFunctionality(res.data?.data || null);
    } catch (e) {
      handleAPIError(e, toast, {});
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async () => {
    setAssignmentsLoading(true);
    try {
      const res = await api.employeeApi.apiVversionFunctionalityFunctionalityIdScopesGet(idNumber, '1', {});
      setAssignments(res.data?.data?.queryResult || []);
    } catch (e) {
      handleAPIError(e, toast, {});
    } finally {
      setAssignmentsLoading(false);
    }
  };

  useEffect(() => {
    fetchFunctionality();
    fetchAssignments();
  }, [idNumber]);

  useEffect(() => {
    api.employeeApi.apiVversionScopeAllGet('1', 1, 500, {})
      .then(res => setScopeOptions(res.data?.data?.queryResult || []))
      .catch(() => {});
  }, []);

  const filteredScopes = scopeOptions.filter(s =>
    !scopeSearchTerm || s.scope?.toLowerCase().includes(scopeSearchTerm.toLowerCase())
  );

  const handleAssign = async () => {
    if (!selectedScope) return;
    try {
      await api.employeeApi.apiVversionFunctionalityScopePost('1', {
        idScope: selectedScope.id,
        idFunctionality: idNumber,
      }, {});
      setSelectedScope(null);
      fetchAssignments();
    } catch (e) {
      handleAPIError(e, toast, {});
    }
  };

  const handleRemove = (assignmentId: number) => {
    const dialogId = openDialog({
      title: intl.get('delete.modal.title'),
      message: intl.get('delete.modal.roleRow.message'),
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get('delete.modal.delete.button'),
        handleConfirm: async () => {
          try {
            await api.employeeApi.apiVversionFunctionalityScopeIdDelete(assignmentId, '1', {});
            fetchAssignments();
          } catch (e) {
            handleAPIError(e, toast, {});
          } finally {
            closeDialog(dialogId);
          }
        },
      },
      cancelButton: {
        cancelLabel: intl.get('delete.modal.cancel.button'),
        handleCancel: () => closeDialog(dialogId),
      },
    });
  };

  const assignmentColumns: GridColDef[] = [
    { field: 'scopeIdentifier', headerName: 'Scope', flex: 1 },
    {
      field: 'remove', headerName: '', width: 80, sortable: false,
      renderCell: (params) => (
        <DefaultActions
          row={params.row}
          entityType={ENTITY_TYPE.ANY}
          onDelete={() => handleRemove(params.row.id)}
          deleteScopes={[]}
        />
      )
    }
  ];

  if (loading) return <Spinner />;
  if (!functionality) return null;

  return (
    <Box className='my-3' sx={{ width: '100%', p: 2 }}>
      <Card sx={{ p: 3, mb: 3 }}>
        <Button startIcon={<KeyboardBackspaceIcon />} onClick={() => navigate('/functionalities')} sx={{ mb: 2 }}>
          Back
        </Button>
        <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
          {functionality.identifier}
        </Typography>
        <Stack spacing={1}>
          <Box><Typography variant="caption" color="text.secondary">Name</Typography><Typography>{functionality.name}</Typography></Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Type</Typography>
            <Box><Chip label={TYPE_LABELS[functionality.type as number] || functionality.type} size="small" /></Box>
          </Box>
          <Box><Typography variant="caption" color="text.secondary">Module</Typography><Typography>{functionality.module}</Typography></Box>
          {functionality.service && <Box><Typography variant="caption" color="text.secondary">Service</Typography><Typography>{functionality.service}</Typography></Box>}
          {functionality.httpMethod && <Box><Typography variant="caption" color="text.secondary">HTTP Method</Typography><Chip label={functionality.httpMethod} size="small" color="primary" variant="outlined" /></Box>}
          {functionality.route && <Box><Typography variant="caption" color="text.secondary">Route</Typography><Typography fontFamily="monospace">{functionality.route}</Typography></Box>}
          {functionality.componentPath && <Box><Typography variant="caption" color="text.secondary">Component Path</Typography><Typography fontFamily="monospace">{functionality.componentPath}</Typography></Box>}
        </Stack>
      </Card>

      <Card sx={{ p: 3 }}>
        <Typography color="primary" variant="h4" sx={{ mb: 1 }}>Scopes that grant access</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Users who have any of these scopes can access this functionality.
        </Typography>

        <Box display="flex" gap={1} mb={2}>
          <Autocomplete
            sx={{ flex: 1 }}
            options={filteredScopes}
            value={selectedScope}
            onChange={(_, val) => setSelectedScope(val)}
            onInputChange={(_, val) => setScopeSearchTerm(val)}
            getOptionLabel={(o) => o.scope || ''}
            renderInput={(params) => <TextField {...params} label="Add scope" size="small" />}
          />
          <Button variant="contained" onClick={handleAssign} disabled={!selectedScope}>
            Assign
          </Button>
        </Box>

        {assignmentsLoading ? <Spinner /> : (
          <DataGrid
            rows={assignments}
            columns={assignmentColumns}
            hideFooter={assignments.length <= 10}
            disableRowSelectionOnClick
          />
        )}
      </Card>
    </Box>
  );
};

export default FunctionalityDetail;
